import React, { memo } from "react";
import normalBus from "../../../assets/images/normal-bus.svg";
import warningBus from "../../../assets/images/warning-bus.svg";
import errorBus from "../../../assets/images/error-bus.svg";
import {
  getDynamicBusLabelSize,
  getDynamicIconSize,
} from "../../liveBusLocations/tools";
import moment from "moment";
import { Box, Typography } from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";

interface IBusMarker {
  assignmentVehicleLocation: IAssignmentWithVehicleLocation;
  school: ISchool;
  lat: number;
  lng: number;
  zoom: number;
  key: string;
  onClick?: () => void;
}

const getBusProperties = (status: string) => {
  switch (status) {
    case "Normal":
      return {
        backgroundColor: "#ffffff",
        color: "#000000",
        busIcon: normalBus,
      };
    case "Deviation":
    case "Delayed":
      return {
        backgroundColor: "#F19012",
        color: "#ffffff",
        busIcon: warningBus,
      };
    case "Breakdown":
    case "Offline":
      return {
        backgroundColor: "#EF0C0C",
        color: "#ffffff",
        busIcon: errorBus,
      };
    default:
      return {
        backgroundColor: "#ffffff",
        color: "#000000",
        busIcon: normalBus,
      };
  }
};

const getBusLabel = (
  assignmentVehicleLocation: IAssignmentWithVehicleLocation,
  fontSize: number,
) => {
  switch (assignmentVehicleLocation.status) {
    case "Normal":
      let lastSeenTextNormal = "-";
      let etaNumber = "-";

      if (assignmentVehicleLocation.vehicle?.lastUpdatedAt) {
        const lastUpdateMoment = moment(
          assignmentVehicleLocation.vehicle.lastUpdatedAt,
        );

        const now = moment();
        const diffInHours = now.diff(lastUpdateMoment, "hours");

        if (diffInHours >= 1) {
          // Show time (e.g., "11:25 AM")
          lastSeenTextNormal = lastUpdateMoment.format("hh:mm A");
        } else {
          // Show relative time (e.g., "2 minutes ago")
          lastSeenTextNormal = lastUpdateMoment.fromNow();
        }
      }

      if (assignmentVehicleLocation.nextStop) {
        etaNumber =
          assignmentVehicleLocation.nextStop.eta > 1
            ? `${assignmentVehicleLocation.nextStop.eta} mins`
            : assignmentVehicleLocation.nextStop.eta === 0
              ? "arrived"
              : `${assignmentVehicleLocation.nextStop.eta} min`;
      }

      return (
        <Box textAlign="center" style={{ padding: "5px 0px" }}>
          {assignmentVehicleLocation.routeName}
          <br />
          <span style={{ fontSize: `${fontSize - 5}px` }}>
            ETA: {etaNumber}{" "}
            {etaNumber === "-" && (
              <WarningIcon
                fontSize="small"
                style={{
                  marginLeft: "4px",
                  marginBottom: "-5px",
                  color: "#d32f2f",
                }}
              />
            )}
          </span>
          <br />
          <span style={{ fontSize: `${fontSize - 5}px` }}>
            Last online: <span>{lastSeenTextNormal}</span>
          </span>
        </Box>
      );
    case "Offline":
      let lastSeenText = "-";

      if (assignmentVehicleLocation.vehicle?.lastUpdatedAt) {
        const lastUpdateMoment = moment(
          assignmentVehicleLocation.vehicle.lastUpdatedAt,
        );

        const now = moment();
        const diffInHours = now.diff(lastUpdateMoment, "hours");

        if (diffInHours >= 1) {
          // Show time (e.g., "11:25 AM")
          lastSeenText = lastUpdateMoment.format("hh:mm A");
        } else {
          // Show relative time (e.g., "2 minutes ago")
          lastSeenText = lastUpdateMoment.fromNow();
        }
      }

      return (
        <Box textAlign="center" style={{ padding: "5px 0px" }}>
          {assignmentVehicleLocation.routeName}
          <br />
          OFFLINE <br />
          <Typography variant="caption" style={{ fontSize: `${fontSize}px` }}>
            Last online: <strong>{lastSeenText}</strong>
          </Typography>
        </Box>
      );
    default:
      return assignmentVehicleLocation.status;
  }
};

const BusMarker = ({
  assignmentVehicleLocation,
  zoom,
  onClick,
}: IBusMarker) => {
  const iconSize = getDynamicIconSize(zoom);
  const busLabelSize = getDynamicBusLabelSize(zoom);
  const busProperties = getBusProperties(assignmentVehicleLocation.status);

  return (
    <div
      style={{
        position: "absolute",
        transform: "translate(-50%, -100%)",
        cursor: "pointer",
        zIndex: 13,
      }}
      onClick={onClick}
    >
      <span
        style={{
          fontSize: `${busLabelSize.fontSize}px`,
          backgroundColor: busProperties.backgroundColor,
          color: busProperties.color,
          fontWeight: "bold",
          textTransform: "uppercase",
          textAlign: "center",
          width: "max-content",
          display: "block",
          padding: `${busLabelSize.padding}px ${busLabelSize.padding + 10}px`,
          boxShadow: "0px 0px 2px rgba(0, 0, 0, 0.25)",
          borderRadius: "4px",
          position: "absolute",
          bottom: "calc(100% + 5px)",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1,
        }}
      >
        {getBusLabel(assignmentVehicleLocation, busLabelSize.fontSize)}
      </span>

      <img
        style={{
          width: `${iconSize}px`, // Dynamic size based on zoom level
          height: `${iconSize}px`, // Dynamic size based on zoom level
        }}
        src={busProperties.busIcon}
        alt="Bus Marker"
      />
    </div>
  );
};

export default memo(BusMarker);
